<script setup lang="ts">
    import AuthLoginForm from '../AuthLoginForm.vue';

    const props = defineProps<{
        type: 'notLoggedIn' | 'forbidden' | 'start' | 'win' | 'lose' | 'error';
        isPending?: boolean;
        isNewHighscore?: boolean;
        highscore?: number;
        points?: number;
        timeUsed?: number;
        startGame?: () => void;
    }>();

    const { t } = useI18n();
</script>

<template>
    <div
        v-if="props.type === 'notLoggedIn'"
        class="memory-modal"
    >
        <div class="memory-modal-content"><AccountLoginForm></AccountLoginForm></div>
    </div>

    <div
        v-if="props.type === 'forbidden'"
        class="memory-modal"
        data-testid="memory-forbidden-modal"
    >
        <AuthLoginForm />
    </div>

    <div
        v-if="props.type === 'start'"
        class="memory-modal"
        data-testid="memory-start-modal"
    >
        <div class="memory-modal-content">
            <h3>{{ t('memoryGame.startModal.heading') }}</h3>
            <p>{{ t('memoryGame.startModal.text') }}</p>
        </div>
        <PrimaryButton
            data-testid="memory-start-button"
            native-button
            :button-text="t('memoryGame.startModal.startButton')"
            @click="props.startGame"
        />
    </div>

    <div
        v-if="props.type === 'win'"
        id="confetti"
        class="memory-modal"
        data-testid="memory-result-modal"
    >
        <div class="memory-modal-content">
            <h3>{{ t('memoryGame.wonModal.heading') }}</h3>
            <p>{{ t('memoryGame.wonModal.text') }}</p>
            <p>
                <strong>{{ t('memoryGame.wonModal.scoreSummaryLabel') }}</strong>
                <span data-testid="memory-result-score">{{ props.points }}</span>
            </p>

            <template v-if="!props.isPending">
                <p v-if="props.isNewHighscore === true">{{ t('memoryGame.wonModal.newHighscore') }}</p>
                <p v-else-if="props.highscore">
                    <strong>{{ t('memoryGame.wonModal.currentHighscoreLabel') }}</strong>
                    <span>{{ props.highscore }}</span>
                </p>
            </template>
            <template v-else>
                <div class="loading-skeleton --line --slim-margin"></div>
                <div class="loading-skeleton --line --slim-margin"></div>
            </template>

            <p>{{ t('memoryGame.wonModal.timeUsed', { timeUsed: props.timeUsed }) }}</p>
        </div>
        <PrimaryButton
            native-button
            :button-text="t('memoryGame.wonModal.button')"
            @click="props.startGame"
        />
    </div>

    <div
        v-if="props.type === 'lose'"
        class="memory-modal"
    >
        <div class="memory-modal-content">
            <h3>{{ t('memoryGame.lostModal.heading') }}</h3>
            <p>{{ t('memoryGame.lostModal.text') }}</p>
            <p>
                <strong>{{ t('memoryGame.lostModal.scoreLabel') }}</strong> <span>{{ props.points }}</span>
            </p>
            <p>{{ t('memoryGame.lostModal.noTimeLeft') }}</p>
        </div>
        <PrimaryButton
            native-button
            :button-text="t('memoryGame.wonModal.button')"
            @click="props.startGame"
        />
    </div>

    <div
        v-if="props.type === 'error'"
        class="memory-modal"
    >
        <div class="memory-modal-content">
            <h3>{{ t('memoryGame.errorModal.heading') }}</h3>
            <p>{{ t('memoryGame.errorModal.text') }}</p>
        </div>
        <PrimaryButton
            native-button
            :button-text="t('memoryGame.wonModal.button')"
            @click="props.startGame"
        />
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/pta-family/memory-game/memory-modal.scss';
</style>
